import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaMobileAlt, FaUsers, FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getBrandsByCompany } from '../../services/companyAuthService';
import { fetchCampaigns } from '../../../Redux/campaignSlice';

const CampaignAdvertising = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaigns, loading } = useSelector((state) => state.campaign);

  // State management
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  useEffect(() => {
    fetchBrands();
    dispatch(fetchCampaigns());
  }, [dispatch]);

  useEffect(() => {
    if (selectedBrand && campaigns.length > 0) {
      const filtered = campaigns.filter(campaign => {
        const brandId = campaign.brandId?._id || campaign.brandId;
        return brandId === selectedBrand;
      });
      setFilteredCampaigns(filtered);
      // Reset campaign selection when brand changes
      setSelectedCampaign(null);
    }
  }, [selectedBrand, campaigns]);

  const fetchBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await getBrandsByCompany();
      if (response.success) {
        setBrands(response.brands);
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to fetch brands");
    } finally {
      setLoadingBrands(false);
    }
  };

  const resetSelection = () => {
    setSelectedBrand('');
    setSelectedCampaign(null);
  };

  const handleOptionClick = (optionType) => {
    if (!selectedCampaign) {
      toast.warning('Please select a campaign first');
      return;
    }

    // Navigate or handle based on option type
    const selectedBrandData = brands.find(b => b._id === selectedBrand);
    if (optionType === 'direct') {
      // Navigate to Add Details page with campaign data
      navigate('/company/dashboard/campaigns/advertising/add-details', {
        state: {
          campaign: selectedCampaign,
          brand: selectedBrandData,
        },
      });
    } else if (optionType === 'agency') {
      // Navigate to Add Agency Details page with campaign data
      navigate('/company/dashboard/campaigns/advertising/add-agency-details', {
        state: {
          campaign: selectedCampaign,
          brand: selectedBrandData,
        },
      });
    }
  };

  if (loading || loadingBrands) {
    return (
      <div className="h-[calc(100dvh-80px)] bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-xs sm:text-sm md:text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 py-8">
      <div className="container mx-auto lg:px-4 px-0 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-orange-500 hover:text-orange-600 transition-colors ml-2"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>

          {(selectedBrand || selectedCampaign) && (
            <button
              onClick={resetSelection}
              className="px-2 py-1 md:px-4 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors mr-2"
            >
              Reset Selection
            </button>
          )}
        </div>

        {/* Page Title */}
        <div className="text-center">
          <h2 className=" text-xl md:text-2xl font-bold text-gray-800 mb-2">Campaign Advertising</h2>
          <p className=" text-gray-600 text-xs md:text-sm lg:text-base p-2 md:p-3 lg:p-4 ">Post a requirement. Relevant partners will connect with you.</p>
        </div>

        {/* Selection Section */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Brand Selection */}
            <div>
              <label htmlFor="brand-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Brand
              </label>
              <select
                id="brand-select"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full p-2  border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="">-- Select a Brand --</option>
                {brands.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.brandName}
                  </option>
                ))}
              </select>
            </div>

            {/* Campaign Selection */}
            <div>
              <label htmlFor="campaign-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Campaign
              </label>
              <select
                id="campaign-select"
                value={selectedCampaign?._id || ''}
                onChange={(e) => {
                  const campaign = filteredCampaigns.find(c => c._id === e.target.value);
                  setSelectedCampaign(campaign);
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={!selectedBrand}
              >
                <option value="">-- Select a Campaign --</option>
                {filteredCampaigns.map((campaign) => (
                  <option key={campaign._id} value={campaign._id}>
                    {campaign.name} - {campaign.campaignType}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Options Section - Only show when both brand and campaign are selected */}
        {selectedBrand && selectedCampaign && (
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-8">
            {/* Header */}
            <div className="mb-8">
              <h3 className=" text-xl md:text-2xl font-bold text-gray-900 mb-2">Who are you looking for?</h3>
              <p className="text-gray-600 text-xs md:text-sm lg:text-base">Post a Requirement. Relevant partners will connect with you.</p>
            </div>

            {/* Options */}
            <div className="space-y-4">
              {/* Option 1: Hire Influencers Directly */}
              <div
                onClick={() => handleOptionClick('direct')}
                className="flex items-center justify-between p-2 md:p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:shadow-lg transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start space-x-3 md:space-x-4 flex-1">

                  {/* Icon Box */}
                  <div className="bg-orange-100 p-2 md:p-4 rounded-lg flex-shrink-0">
                    <FaMobileAlt className="text-orange-600 text-xl md:text-2xl" />
                  </div>

                  {/* Text Section */}
                  <div className="flex-1">
                    <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-1 leading-tight">
                      Hire Influencers Directly
                    </h4>

                    <p className="text-xs md:text-sm text-gray-600 leading-snug">
                      Get influencers directly — no agencies involved.
                    </p>

                    <p className="text-xs md:text-sm text-gray-500 mt-1 leading-snug">
                      *Weekend responses may take slightly longer.
                    </p>
                  </div>
                </div>

                {/* Arrow Icon */}
                <div className="ml-1 md:ml-2 flex-shrink-0">
                  <FaArrowRight className="text-gray-400 group-hover:text-orange-500 transition-colors text-lg md:text-xl" />
                </div>
              </div>


              {/* Option 2: Influencer Marketing Agencies */}
              {/* <div
                onClick={() => handleOptionClick('agency')}
                className="flex items-center justify-between p-6 lg:bg-white border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:shadow-lg transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center space-x-2 md:space-x-3 flex-1">
                  <div className="bg-blue-100 p-4 rounded-lg">
                    <FaUsers className="text-blue-600 text-2xl" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 mb-1">Influencer Marketing Agencies</h4>
                    <p className="text-xs md:text-sm lg:text-base text-gray-600">Connect with professional marketing agencies for your campaign.</p>
                  </div>
                </div>
                <div className="ml-4">
                  <FaArrowRight className="text-gray-400 group-hover:text-orange-500 transition-colors text-xl" />
                </div>
              </div> */}
              <div
                onClick={() => handleOptionClick('agency')}
                className="flex items-center justify-between p-2 md:p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:shadow-lg transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start space-x-3 md:space-x-4 flex-1">
                  {/* Icon Box */}
                  <div className="bg-blue-100 p-2 md:p-4 rounded-lg flex-shrink-0">
                    <FaUsers className="text-blue-600 text-xl md:text-2xl" />
                  </div>

                  {/* Text Section */}
                  <div className="flex-1">
                    <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-1 leading-tight">
                      Influencer Marketing Agencies
                    </h4>

                    <p className="text-xs md:text-sm text-gray-600 leading-snug">
                      Partner with expert agencies to manage and grow your campaign efficiently.
                    </p>
                  </div>
                </div>

                {/* Arrow Icon */}
                  <div className="ml-1 md:ml-2 flex-shrink-0">
                  <FaArrowRight className="text-gray-400 group-hover:text-orange-500 transition-colors text-lg md:text-xl" />
                </div>
              </div>

            </div>
          </div>
        )}

        {/* Empty State - When no selection */}
        {!selectedBrand && !selectedCampaign && (
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-8 text-center">
            <div className="text-gray-400 mb-4">
              <FaUsers className="text-6xl mx-auto" />
            </div>
            <h3 className="text-lg md:text-lg lg:text-xl font-semibold text-gray-700 mb-2">Select Brand and Campaign</h3>
            <p className="text-gray-500 text-xs md:text-sm lg:text-base">Please select a brand and campaign to view advertising options.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignAdvertising;
